// FILE: src/pages/AuthPage.jsx
// Login / Register / Verify / Forgot-password.
// Logic (state, handlers, API calls, localStorage flow) is unchanged from the
// original; only the presentation is redesigned (clean, light, white + brand
// yellow). Styling is fully scoped under `.authx-root` and injected inline, so
// it no longer leaks onto the rest of the app.

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api";

const AX_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

.authx-root {
  --yellow: #FFCE1F;
  --yellow-hi: #FFD84D;
  --yellow-deep: #FFB703;
  --ink: #1b1408;
  --text: #2c2617;
  --muted: #7c745f;
  --line: #ece3cf;
  --paper: #ffffff;
  --cream: #FFFBF0;
  --cream-2: #FFF6DE;
  position: fixed; inset: 0; z-index: 1;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: var(--text); background: var(--paper);
  display: grid; grid-template-columns: 1.02fr 1fr;
  overflow-y: auto; -webkit-font-smoothing: antialiased;
}
.authx-root *, .authx-root *::before, .authx-root *::after { box-sizing: border-box; }
.authx-root h1, .authx-root h2, .authx-root h3, .authx-root p, .authx-root label, .authx-root ul, .authx-root li { margin: 0; padding: 0; list-style: none; }
.authx-root a { color: inherit; text-decoration: none; cursor: pointer; }
.authx-root button { font-family: inherit; cursor: pointer; border: none; background: none; }
.authx-root form { display: block; gap: 0; margin-top: 0; }
.authx-root img { max-width: 100%; display: block; }

/* ---------- Brand panel (light) ---------- */
.ax-brand-panel {
  position: relative; overflow: hidden; padding: 44px 48px;
  background: linear-gradient(158deg, #FFFEF8 0%, #FFF0BE 50%, #FFE195 100%);
  border-right: 1px solid var(--line);
  display: flex; flex-direction: column; justify-content: space-between; gap: 22px;
}
.ax-brand-panel::before {
  content: ""; position: absolute; inset: 0; pointer-events: none; z-index: 0;
  background:
    radial-gradient(460px 320px at 88% 4%, rgba(255,183,3,.30), transparent 62%),
    radial-gradient(420px 340px at 2% 98%, rgba(255,206,31,.26), transparent 60%);
}
.ax-brand-panel::after {
  content: ""; position: absolute; inset: 0; pointer-events: none; opacity: .55; z-index: 0;
  background-image: radial-gradient(rgba(180,140,20,.13) 1.4px, transparent 1.4px);
  background-size: 22px 22px;
  mask-image: radial-gradient(circle at 45% 40%, #000, transparent 80%);
  -webkit-mask-image: radial-gradient(circle at 45% 40%, #000, transparent 80%);
}
.ax-blob { position: absolute; border-radius: 50%; pointer-events: none; z-index: 0; filter: blur(1px); }
.ax-blob-1 { width: 190px; height: 190px; top: -46px; right: -34px; background: radial-gradient(circle at 34% 30%, rgba(255,255,255,.7), rgba(255,206,31,.22)); }
.ax-blob-2 { width: 120px; height: 120px; bottom: 120px; left: -34px; background: radial-gradient(circle at 34% 30%, rgba(255,255,255,.6), rgba(255,183,3,.18)); }

.ax-logo-chip { position: relative; z-index: 2; align-self: flex-start; background: #fff; border: 1px solid #f7e7b4; border-radius: 14px; padding: 9px 15px; box-shadow: 0 10px 24px rgba(180,140,20,.15); }
.ax-logo { display: block; height: 32px; width: auto; max-width: 150px; object-fit: contain; }

.ax-mid-group { position: relative; z-index: 2; display: flex; flex-direction: column; gap: 20px; }
.ax-brand-mid h2 { font-size: clamp(25px, 2.6vw, 33px); font-weight: 800; letter-spacing: -0.03em; line-height: 1.12; color: var(--ink); }
.ax-brand-mid h2 .hl { position: relative; white-space: nowrap; z-index: 0; }
.ax-brand-mid h2 .hl::after { content: ""; position: absolute; left: -3px; right: -3px; bottom: 6%; height: 34%; z-index: -1; background: var(--yellow); border-radius: 5px; transform: rotate(-1.5deg); }
.ax-brand-mid p { margin-top: 13px; font-size: 15px; color: #6f6749; max-width: 380px; }

/* Floating product card */
.ax-stage { position: relative; z-index: 2; display: flex; justify-content: center; padding: 6px 8px 20px; }
.ax-pcard { position: relative; width: 100%; max-width: 336px; background: #fff; border-radius: 20px; border: 1px solid #f4e7bd; box-shadow: 0 26px 52px rgba(150,110,10,.20); padding: 18px; transform: rotate(-2deg); animation: ax-cardfloat 7s ease-in-out infinite; }
@keyframes ax-cardfloat { 0%,100% { transform: rotate(-2deg) translateY(0); } 50% { transform: rotate(-2deg) translateY(-9px); } }
.ax-pc-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.ax-pc-acct { display: flex; align-items: center; gap: 10px; }
.ax-pc-icon { width: 38px; height: 38px; border-radius: 11px; background: linear-gradient(150deg, var(--yellow-hi), var(--yellow-deep)); display: grid; place-items: center; color: #1c1500; }
.ax-pc-acct b { font-size: 14px; font-weight: 700; color: var(--ink); display: block; }
.ax-pc-acct span { font-size: 11.5px; color: var(--muted); }
.ax-pc-badge { font-size: 11px; font-weight: 700; padding: 5px 10px; border-radius: 999px; background: var(--yellow); color: #1c1500; display: inline-flex; align-items: center; gap: 5px; }
.ax-pc-balance { font-size: 30px; font-weight: 800; letter-spacing: -0.03em; color: var(--ink); }
.ax-pc-balance small { font-size: 12.5px; color: var(--muted); font-weight: 600; margin-left: 6px; }
.ax-pc-bars { display: flex; align-items: flex-end; gap: 6px; height: 44px; margin: 12px 0 2px; }
.ax-pc-bars i { flex: 1; border-radius: 5px 5px 2px 2px; background: linear-gradient(180deg, var(--yellow), rgba(255,183,3,.4)); display: block; }
.ax-pc-denoms { margin-top: 12px; border-top: 1px dashed #f0e3bb; padding-top: 12px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.ax-pc-denom { background: var(--cream); border: 1px solid #f2e6c0; border-radius: 10px; padding: 7px 8px; }
.ax-pc-denom b { display: block; font-size: 12.5px; font-weight: 700; color: var(--ink); }
.ax-pc-denom span { font-size: 10px; color: var(--muted); }
.ax-pc-badge2 { position: absolute; bottom: -16px; right: -10px; background: #fff; border: 1px solid #f4e7bd; border-radius: 13px; box-shadow: 0 14px 28px rgba(150,110,10,.2); padding: 9px 12px; display: flex; align-items: center; gap: 9px; transform: rotate(4deg); animation: ax-badgefloat 8s ease-in-out infinite .4s; }
@keyframes ax-badgefloat { 0%,100% { transform: rotate(4deg) translateY(0); } 50% { transform: rotate(4deg) translateY(-7px); } }
.ax-pc-badge2 .fi { width: 28px; height: 28px; border-radius: 9px; display: grid; place-items: center; color: #1c1500; background: linear-gradient(150deg, var(--yellow-hi), var(--yellow-deep)); flex: 0 0 auto; }
.ax-pc-badge2 b { font-size: 12px; font-weight: 700; color: var(--ink); display: block; }
.ax-pc-badge2 span { font-size: 10px; color: var(--muted); }

/* Trust chips */
.ax-hi { position: relative; z-index: 2; display: grid; gap: 9px; }
.ax-hi-item { display: flex; align-items: center; gap: 12px; background: rgba(255,255,255,.62); border: 1px solid #f6e9c0; border-radius: 13px; padding: 10px 13px; -webkit-backdrop-filter: blur(4px); backdrop-filter: blur(4px); }
.ax-hi-item .c { width: 30px; height: 30px; flex: 0 0 auto; border-radius: 9px; display: grid; place-items: center; color: #1c1500; background: linear-gradient(150deg, var(--yellow-hi), var(--yellow-deep)); box-shadow: 0 5px 12px rgba(255,183,3,.3); }
.ax-hi-item b { font-size: 13.5px; font-weight: 700; color: var(--ink); letter-spacing: -0.01em; display: block; }
.ax-hi-item span { font-size: 12px; color: #857a58; display: block; }

@media (prefers-reduced-motion: reduce) {
  .ax-pcard, .ax-pc-badge2 { animation: none; }
}

/* ---------- Form side ---------- */
.ax-form-side { display: flex; align-items: center; justify-content: center; padding: 40px 28px; background: var(--paper); }
.ax-card { width: 100%; max-width: 440px; }
.ax-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 26px; }
.ax-back { font-size: 14px; font-weight: 600; color: var(--muted); display: inline-flex; align-items: center; gap: 7px; }
.ax-back:hover { color: var(--yellow-deep); }
.ax-logo-sm { display: none; height: 32px; width: auto; }

.ax-tabs { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; background: var(--cream); border: 1px solid var(--line); padding: 5px; border-radius: 14px; margin-bottom: 26px; }
.ax-tab { padding: 11px; border-radius: 10px; font-size: 14.5px; font-weight: 700; color: var(--muted); letter-spacing: -0.01em; transition: all .18s; }
.ax-tab.active { background: var(--paper); color: var(--ink); box-shadow: 0 2px 8px rgba(60,48,12,.1); }

.ax-title { font-size: 26px; font-weight: 800; letter-spacing: -0.02em; color: var(--ink); }
.ax-desc { margin-top: 7px; font-size: 14.5px; color: var(--muted); margin-bottom: 24px; }

.ax-group { margin-bottom: 16px; }
.ax-group label { display: block; font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 7px; }
.ax-input {
  width: 100%; padding: 13px 15px; font-size: 15px; color: var(--ink);
  background: var(--paper); border: 1.5px solid var(--line); border-radius: 12px; outline: none;
  transition: border-color .15s, box-shadow .15s; font-family: inherit;
}
.ax-input::placeholder { color: #b3a988; }
.ax-input:focus { border-color: var(--yellow-deep); box-shadow: 0 0 0 4px rgba(255,206,31,.28); }
.ax-input[readonly] { background: var(--cream); color: var(--muted); }

.ax-btn {
  width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 14px 20px; font-size: 15.5px; font-weight: 700; letter-spacing: -0.01em; border-radius: 12px;
  transition: transform .16s, box-shadow .16s;
}
.ax-btn-primary { background: linear-gradient(180deg, var(--yellow-hi), var(--yellow-deep)); color: #1c1500; box-shadow: 0 10px 24px rgba(255,183,3,.32); }
.ax-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 14px 30px rgba(255,183,3,.44); }
.ax-btn-block { margin-top: 8px; }
.authx-root .ax-btn-ghost { background: var(--cream); color: var(--ink); border: 1.5px solid var(--line); }
.authx-root .ax-btn-ghost:hover { border-color: var(--yellow-deep); color: var(--yellow-deep); }
.ax-btn-sm { width: auto; padding: 11px 18px; font-size: 14px; }

.ax-forgot { margin-top: 16px; text-align: center; }
.ax-forgot a { font-size: 14px; font-weight: 600; color: var(--yellow-deep); }
.ax-forgot a:hover { text-decoration: underline; }

.ax-msg { margin-top: 16px; padding: 12px 14px; border-radius: 11px; font-size: 13.5px; font-weight: 500; background: var(--cream-2); border: 1px solid #f3e2a6; color: #7a5c00; }

.ax-pending { margin-top: 22px; padding-top: 20px; border-top: 1px dashed var(--line); text-align: center; }
.ax-pending p { font-size: 13px; color: var(--muted); margin-bottom: 10px; }

/* ---------- Modal ---------- */
.ax-overlay { position: fixed; inset: 0; z-index: 60; background: rgba(40,30,4,.42); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: ax-fade .18s ease; }
@keyframes ax-fade { from { opacity: 0; } to { opacity: 1; } }
.ax-modal { width: 100%; max-width: 420px; background: var(--paper); border-radius: 20px; padding: 28px; box-shadow: 0 30px 70px rgba(40,30,4,.28); animation: ax-pop .2s cubic-bezier(.2,.7,.2,1); }
@keyframes ax-pop { from { opacity: 0; transform: translateY(14px) scale(.98); } to { opacity: 1; transform: none; } }
.ax-modal-head { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 6px; }
.ax-modal-head h3 { font-size: 20px; font-weight: 800; letter-spacing: -0.02em; color: var(--ink); }
.ax-modal-head button { font-size: 22px; line-height: 1; color: var(--muted); padding: 0 4px; }
.ax-modal-head button:hover { color: var(--ink); }
.ax-modal p.ax-modal-sub { font-size: 14px; color: var(--muted); margin-bottom: 20px; }
.ax-code-input { text-align: center; letter-spacing: .5em; font-size: 22px; font-weight: 700; padding-left: .5em; }

@media (max-width: 860px) {
  .authx-root { grid-template-columns: 1fr; }
  .ax-brand-panel { display: none; }
  .ax-logo-sm { display: block; }
}
`;

const AXI = {
  check: (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>),
  back: (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 18l-6-6 6-6"/></svg>),
  wallet: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7a2 2 0 0 1 2-2h13a1 1 0 0 1 1 1v2"/><path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-8a1 1 0 0 0-1-1H5a2 2 0 0 1-2-2Z"/><circle cx="16" cy="13" r="1.4"/></svg>),
};

function AuthPage() {
  const navigate = useNavigate();

  // Tab state: "login" | "register"
  const [activeTab, setActiveTab] = useState("login");

  // LOGIN form fields
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginMessage, setLoginMessage] = useState("");

  // REGISTER form fields
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regOrgName, setRegOrgName] = useState("");
  const [regMessage, setRegMessage] = useState("");

  // VERIFICATION state variables
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyMessage, setVerifyMessage] = useState("");

  // FORGOT PASSWORD state variables
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");

  // Pending verification state
  const [pendingEmailExists, setPendingEmailExists] = useState(false);

  // Check on mount if there's a "pendingVerifyEmail" in localStorage
  useEffect(() => {
    const storedEmail = localStorage.getItem("pendingVerifyEmail");
    if (storedEmail) {
      setPendingEmailExists(true);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // HANDLE LOGIN
  // ─────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginMessage("");

    try {
      const res = await API.post("/auth/login", {
        username: loginEmail,
        password: loginPassword,
      });
      setLoginMessage(res.data.message || "Login successful.");

      // Store token
      if (res.data.token) {
        localStorage.setItem("token", res.data.token);
      }
      // Store role
      if (res.data.user && res.data.user.role) {
        localStorage.setItem("userRole", res.data.user.role);
      }

      // Navigate to your main app after successful login:
      window.location.href = "/dashboard";
    } catch (error) {
      setLoginMessage(error.response?.data?.message || error.message);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // HANDLE REGISTER
  // ─────────────────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    setRegMessage("");

    try {
      const response = await API.post("/auth/register", {
        username: regEmail,
        password: regPassword,
        orgName: regOrgName,
      });
      setRegMessage(response.data.message || "Registration successful.");

      // If success, open the verify modal:
      setShowVerifyModal(true);

      // Store the newly registered email in localStorage so user can finish verifying
      localStorage.setItem("pendingVerifyEmail", regEmail);
      setPendingEmailExists(true);

      // Fill the verifyEmail in the modal automatically
      setVerifyEmail(regEmail);
    } catch (error) {
      setRegMessage(error.response?.data?.message || error.message);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // HANDLE VERIFICATION
  // ─────────────────────────────────────────────────────────────────
  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifyMessage("");

    try {
      const res = await API.post("/auth/verify", {
        username: verifyEmail,
        code: verifyCode,
      });
      setVerifyMessage(res.data.message || "Verification successful.");

      // If successful, remove pending email and reset state
      localStorage.removeItem("pendingVerifyEmail");
      setPendingEmailExists(false);

      // Optionally auto-close the modal after a short delay:
      setTimeout(() => {
        setShowVerifyModal(false);
        // After verifying, switch them to the login tab:
        setActiveTab("login");
      }, 1500);
    } catch (error) {
      setVerifyMessage(error.response?.data?.message || error.message);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // HANDLE FORGOT PASSWORD
  // ─────────────────────────────────────────────────────────────────
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotMessage("");

    try {
      const response = await API.post("/users/forgot-password", {
        username: forgotEmail,
      });

      setForgotMessage(
        response.data.message || "Reset email sent. Please check your inbox."
      );
    } catch (error) {
      setForgotMessage(error.response?.data?.message || error.message);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // Utility to open the verification modal if pendingEmail exists
  // ─────────────────────────────────────────────────────────────────
  const handleOpenPendingVerify = () => {
    const storedEmail = localStorage.getItem("pendingVerifyEmail");
    if (storedEmail) {
      setVerifyEmail(storedEmail);
      setShowVerifyModal(true);
    }
  };

  return (
    <div className="authx-root">
      <style>{AX_CSS}</style>

      {/* LEFT: brand panel */}
      <aside className="ax-brand-panel">
        <span className="ax-blob ax-blob-1" />
        <span className="ax-blob ax-blob-2" />

        <span className="ax-logo-chip"><img className="ax-logo" src="/olo.png" alt="CashPro" /></span>

        <div className="ax-mid-group">
          <div className="ax-brand-mid">
            <h2>Balance the drawer.<br /><span className="hl">Every single day.</span></h2>
            <p>The friendly, complete cash-handling and reconciliation system for cash-first businesses — from till shift to bank.</p>
          </div>

          <div className="ax-stage">
            <div className="ax-pcard">
              <div className="ax-pc-top">
                <div className="ax-pc-acct">
                  <span className="ax-pc-icon">{AXI.wallet}</span>
                  <div><b>Main Till</b><span>Account · GBP</span></div>
                </div>
                <span className="ax-pc-badge">{AXI.check} Balanced</span>
              </div>
              <div className="ax-pc-balance">£4,820.00<small>balance</small></div>
              <div className="ax-pc-bars" aria-hidden="true">
                <i style={{ height: "42%" }} /><i style={{ height: "64%" }} /><i style={{ height: "50%" }} />
                <i style={{ height: "80%" }} /><i style={{ height: "58%" }} /><i style={{ height: "88%" }} /><i style={{ height: "68%" }} />
              </div>
              <div className="ax-pc-denoms">
                <div className="ax-pc-denom"><b>£50×40</b><span>£2,000</span></div>
                <div className="ax-pc-denom"><b>£20×96</b><span>£1,920</span></div>
                <div className="ax-pc-denom"><b>£10×90</b><span>£900</span></div>
              </div>
              <div className="ax-pc-badge2">
                <span className="fi">{AXI.check}</span>
                <div><b>Variance £0.00</b><span>counted = expected</span></div>
              </div>
            </div>
          </div>
        </div>

        <div className="ax-hi">
          <div className="ax-hi-item"><span className="c">{AXI.check}</span><div><b>Denomination-level reconciliation</b><span>Count the drawer, book the variance</span></div></div>
          <div className="ax-hi-item"><span className="c">{AXI.check}</span><div><b>Role-based access</b><span>Admin, Manager, Accountant &amp; Staff</span></div></div>
          <div className="ax-hi-item"><span className="c">{AXI.check}</span><div><b>Full audit log</b><span>Every action, per organisation</span></div></div>
        </div>
      </aside>

      {/* RIGHT: forms */}
      <main className="ax-form-side">
        <div className="ax-card">
          <div className="ax-top">
            <a className="ax-back" onClick={() => navigate("/")}>{AXI.back} Back to home</a>
            <img className="ax-logo-sm" src="/olo.png" alt="CashPro" />
          </div>

          <div className="ax-tabs" role="tablist">
            <button
              className={`ax-tab ${activeTab === "login" ? "active" : ""}`}
              onClick={() => setActiveTab("login")}
              type="button"
            >
              Log in
            </button>
            <button
              className={`ax-tab ${activeTab === "register" ? "active" : ""}`}
              onClick={() => setActiveTab("register")}
              type="button"
            >
              Register
            </button>
          </div>

          {/* LOGIN TAB */}
          {activeTab === "login" && (
            <form onSubmit={handleLogin}>
              <h1 className="ax-title">Welcome back 👋</h1>
              <p className="ax-desc">Enter your credentials to access your account.</p>

              <div className="ax-group">
                <label>Email</label>
                <input
                  className="ax-input"
                  type="email"
                  required
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="yourname@example.com"
                />
              </div>
              <div className="ax-group">
                <label>Password</label>
                <input
                  className="ax-input"
                  type="password"
                  required
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>

              <button type="submit" className="ax-btn ax-btn-primary ax-btn-block">Log in</button>

              {loginMessage && <div className="ax-msg">{loginMessage}</div>}

              <div className="ax-forgot">
                <a
                  onClick={() => {
                    setForgotEmail(loginEmail);
                    setShowForgotModal(true);
                  }}
                >
                  Forgot password?
                </a>
              </div>
            </form>
          )}

          {/* REGISTER TAB */}
          {activeTab === "register" && (
            <form onSubmit={handleRegister}>
              <h1 className="ax-title">Create your account</h1>
              <p className="ax-desc">Register a new organisation admin account.</p>

              <div className="ax-group">
                <label>Email</label>
                <input
                  className="ax-input"
                  type="email"
                  required
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  placeholder="newuser@example.com"
                />
              </div>
              <div className="ax-group">
                <label>Password</label>
                <input
                  className="ax-input"
                  type="password"
                  required
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  placeholder="Choose a strong password"
                />
              </div>
              <div className="ax-group">
                <label>Organisation name</label>
                <input
                  className="ax-input"
                  type="text"
                  value={regOrgName}
                  onChange={(e) => setRegOrgName(e.target.value)}
                  placeholder="E.g. My Company Ltd"
                />
              </div>

              <button type="submit" className="ax-btn ax-btn-primary ax-btn-block">Create account</button>

              {regMessage && <div className="ax-msg">{regMessage}</div>}
            </form>
          )}

          {/* Pending verification */}
          {pendingEmailExists && (
            <div className="ax-pending">
              <p>Started registering but didn't verify?</p>
              <button className="ax-btn ax-btn-ghost ax-btn-sm" onClick={handleOpenPendingVerify}>
                Finish email verification
              </button>
            </div>
          )}
        </div>
      </main>

      {/* FORGOT PASSWORD MODAL */}
      {showForgotModal && (
        <div
          className="ax-overlay"
          onClick={() => {
            setShowForgotModal(false);
            setForgotMessage("");
          }}
        >
          <div className="ax-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ax-modal-head">
              <h3>Forgot password</h3>
              <button
                type="button"
                aria-label="Close"
                onClick={() => {
                  setShowForgotModal(false);
                  setForgotMessage("");
                }}
              >
                ×
              </button>
            </div>
            <p className="ax-modal-sub">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={handleForgotPassword}>
              <div className="ax-group">
                <label>Email</label>
                <input
                  className="ax-input"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  placeholder="yourname@example.com"
                />
              </div>
              {forgotMessage && <div className="ax-msg">{forgotMessage}</div>}
              <button type="submit" className="ax-btn ax-btn-primary ax-btn-block">Send reset link</button>
            </form>
          </div>
        </div>
      )}

      {/* VERIFICATION MODAL */}
      {showVerifyModal && (
        <div className="ax-overlay" onClick={() => setShowVerifyModal(false)}>
          <div className="ax-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ax-modal-head">
              <h3>Verify your email</h3>
              <button type="button" aria-label="Close" onClick={() => setShowVerifyModal(false)}>×</button>
            </div>
            <p className="ax-modal-sub">Enter the 6-digit code we sent to your email.</p>
            <form onSubmit={handleVerify}>
              <div className="ax-group">
                <label>Email</label>
                <input
                  className="ax-input"
                  type="email"
                  value={verifyEmail}
                  onChange={(e) => setVerifyEmail(e.target.value)}
                  readOnly
                />
              </div>
              <div className="ax-group">
                <label>6-digit code</label>
                <input
                  className="ax-input ax-code-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  required
                  placeholder="123456"
                />
              </div>
              {verifyMessage && <div className="ax-msg">{verifyMessage}</div>}
              <button type="submit" className="ax-btn ax-btn-primary ax-btn-block">Verify</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuthPage;
